import React from 'react';
import {useField} from 'react-final-form';
import {useIntl} from 'react-intl';
import cn from 'classnames';

import {useGeneralProvider} from 'brandComponents/_app-config/GeneralProvider';
import BirthDateFields, {useBirthdayValidator} from 'brandComponents/Form/Subcomponents/BirthDateFields';
import Field, {formatRule} from 'brandComponents/Form/Subcomponents/Field';
import {createPasswordValidation} from 'brandComponents/Form/Subcomponents/PasswordField/passwordValidation';
import PasswordToggle from 'brandComponents/Form/Subcomponents/PasswordToggle';
import Icon from 'brandComponents/Icon';
import PasswordField from 'brandComponents/PasswordField';
import PhoneField from 'brandComponents/PhoneField/PhoneField';
import {DEFAULT_BIRTH_RANGE, INPUT_MAX_LENGTH, PHONE_MAX_LENGTH} from 'brandConstants';
import usePasswordLoop from 'brandHooks/usePasswordLoop';

import {IAuthRegisterFormFieldsProps} from './IAuthRegisterFormFields';
import messages from './messages';

import styles from './AuthRegisterFormFields.module.scss';

const AuthRegisterFormFields = ({classNames, schema}: IAuthRegisterFormFieldsProps) => {
	const {managerRepository} = useGeneralProvider();
	const [passwordType, togglePasswordType] = usePasswordLoop();
	const intl = useIntl();

	const {meta: firstNameMeta} = useField('firstName', {
		subscription: {touched: true, error: true},
	});

	const {meta: lastNameMeta} = useField('lastName', {
		subscription: {touched: true, error: true},
	});

	const {meta: emailMeta} = useField('email', {
		subscription: {touched: true, error: true},
	});

	const {meta: passwordMeta} = useField('password', {
		subscription: {touched: true, error: true},
	});

	const {meta: phoneMeta} = useField('phone', {
		subscription: {touched: true, error: true}
	});

	const passwordConfig = managerRepository.getConfigurationsMgr().get('passwordConfig');
	const passwordValidation = createPasswordValidation(passwordConfig, messages);

	const hasFirstNameError = firstNameMeta.touched && firstNameMeta.error;
	const hasLastNameError = lastNameMeta.touched && lastNameMeta.error;
	const hasEmailError = emailMeta.touched && emailMeta.error;
	const hasPasswordError = passwordMeta.touched && passwordMeta.error;
	const hasPhoneError = phoneMeta.touched && phoneMeta.error;
	const {hasBirthdayError} = useBirthdayValidator('birthday');

	return (
		<div className={cn(styles['auth__register-form__fields'], classNames)}>
			<Field
				component="input"
				id="register-first-name"
				name="firstName"
				type="text"
				label={intl.formatMessage(messages.firstNameLabel)}
				classNamesCntr={cn({['has-error']: hasFirstNameError})}
				validation={schema.rules.firstName.map(formatRule(intl))}
				maxLength={INPUT_MAX_LENGTH}
				requirednessLabel="*"
			/>
			<Field
				component="input"
				id="register-last-name"
				name="lastName"
				type="text"
				label={intl.formatMessage(messages.lastNameLabel)}
				classNamesCntr={cn({['has-error']: hasLastNameError})}
				validation={schema.rules.lastName.map(formatRule(intl))}
				maxLength={INPUT_MAX_LENGTH}
				requirednessLabel="*"
			/>
			<BirthDateFields
				name="birthday"
				error={hasBirthdayError}
				yearsOfBirthRange = {DEFAULT_BIRTH_RANGE}
				label={intl.formatMessage(messages.birthdayLabel, {
					required: (chunk) => (
						<span className="d-none" role="none" aria-hidden="true">
							{chunk}
						</span>
					),
				}) as string}
				classNames={cn('field', {'has-error': !!hasBirthdayError})}
				customIcon={<Icon classNames={styles['auth__register-form__fields--icon']} name="chevron-down" width={10} height={10} />}
			/>
			<PhoneField
				classNames={cn('field', {'has-error': !!hasPhoneError})}
				showElement={{label: true, tip: false}}
				isRequired={false}
				maxLength={PHONE_MAX_LENGTH}
				customIcon={<Icon classNames={styles['auth__register-form__fields--icon']} name="chevron-down" width={10} height={10} />}
			/>
			<Field
				component="input"
				id="register-email"
				name="email"
				type="email"
				label={intl.formatMessage(messages.emailLabel)}
				classNamesCntr={cn({['has-error']: hasEmailError})}
				validation={schema.rules.email.map(formatRule(intl))}
				maxLength={INPUT_MAX_LENGTH}
				requirednessLabel="*"
			/>
			<PasswordField
				component="input"
				id="register-password"
				name="password"
				type={passwordType}
				label={intl.formatMessage(messages.passwordLabel)}
				requirednessLabel="*"
				customIcon={<PasswordToggle value={passwordType} onClick={togglePasswordType} />}
				classNamesCntr={cn({['has-error']: hasPasswordError})}
				passwordValidation={passwordValidation}
				maxLength={passwordConfig.maxLength}
			/>
		</div>
	);
};

export default AuthRegisterFormFields;
